import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuditEmphasisOfMatter } from '../../../models/auditEmphasisOfMatter';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-audit-emphasis-of-matter-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './audit-emphasis-of-matter-list.component.html',
  styleUrl: './audit-emphasis-of-matter-list.component.scss'
})
export class AuditEmphasisOfMatterListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  auditEmphasisOfMatters = signal<AuditEmphasisOfMatter[]>([]);
  allAuditEmphasisOfMatters: AuditEmphasisOfMatter[] = [];
  rightItem: AuditEmphasisOfMatter | undefined = undefined;
  matchedItems: AuditEmphasisOfMatter[] | undefined = undefined;
  isSearchVisible = false;
  searchFilter: LookupTableSearchFilter = {};

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAuditEmphasisOfMatters();
  }

  loadAuditEmphasisOfMatters(): void {
    this.spinnerService.show();
    this.lutService.getAuditEmphasisOfMatters().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortAuditEmphasisOfMatters(data);
        this.allAuditEmphasisOfMatters = sortedData;
        this.auditEmphasisOfMatters.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading audit emphasis of matters:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortAuditEmphasisOfMatters(auditEmphasisOfMatters: AuditEmphasisOfMatter[]): AuditEmphasisOfMatter[] {
    return [...auditEmphasisOfMatters].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateAuditEmphasisOfMatter(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.searchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onItemSelected(auditEmphasisOfMatter: AuditEmphasisOfMatter): void {
  }


  onEditAuditEmphasisOfMatter(auditEmphasisOfMatter: AuditEmphasisOfMatter): void {
  }

  onDeleteAuditEmphasisOfMatter(auditEmphasisOfMatter: AuditEmphasisOfMatter): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(auditEmphasisOfMatter.name)
      .subscribe(result => {
        if (result) {
          // Store original lists for rollback in case of error
          const originalAuditEmphasisOfMatters = this.auditEmphasisOfMatters();
          const originalAllAuditEmphasisOfMatters = this.allAuditEmphasisOfMatters;

          // 1. Optimistically remove from local state immediately
          this.auditEmphasisOfMatters.update(auditEmphasisOfMatters => auditEmphasisOfMatters.filter(aeom => aeom.id !== auditEmphasisOfMatter.id));
          this.allAuditEmphasisOfMatters = this.allAuditEmphasisOfMatters.filter(aeom => aeom.id !== auditEmphasisOfMatter.id);

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteAuditEmphasisOfMatter(auditEmphasisOfMatter.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Audit emphasis of matter deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting audit emphasis of matter:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original lists
              this.auditEmphasisOfMatters.set(originalAuditEmphasisOfMatters);
              this.allAuditEmphasisOfMatters = originalAllAuditEmphasisOfMatters;
              this.toast.error(`<strong>Failed to delete Audit Emphasis Of Matter:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AuditEmphasisOfMatter, newName: string} | {item: AuditEmphasisOfMatter, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };
    const originalAllAuditEmphasisOfMatters = this.allAuditEmphasisOfMatters;

    // Prepare updated item
    let updatedItem: AuditEmphasisOfMatter;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.auditEmphasisOfMatters.update(auditEmphasisOfMatters => {
      const updated = auditEmphasisOfMatters.map(aeom => aeom.id === event.item.id ? updatedItem : aeom);
      return this.sortAuditEmphasisOfMatters(updated);
    });
    this.allAuditEmphasisOfMatters = this.sortAuditEmphasisOfMatters(
      this.allAuditEmphasisOfMatters.map(aeom => aeom.id === event.item.id ? updatedItem : aeom)
    );

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateAuditEmphasisOfMatter(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit emphasis of matter updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.auditEmphasisOfMatters.update(auditEmphasisOfMatters => {
          const updated = auditEmphasisOfMatters.map(aeom => aeom.id === event.item.id ? response : aeom);
          return this.sortAuditEmphasisOfMatters(updated);
        });
        this.allAuditEmphasisOfMatters = this.sortAuditEmphasisOfMatters(
          this.allAuditEmphasisOfMatters.map(aeom => aeom.id === event.item.id ? response : aeom)
        );
      },
      error: (error) => {
        console.error('Error updating audit emphasis of matter:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.auditEmphasisOfMatters.update(auditEmphasisOfMatters =>
          auditEmphasisOfMatters.map(aeom => aeom.id === event.item.id ? originalItem : aeom)
        );
        this.allAuditEmphasisOfMatters = originalAllAuditEmphasisOfMatters;
        this.toast.error(`<strong>Failed to update Audit Emphasis Of Matter:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newAuditEmphasisOfMatter: AuditEmphasisOfMatter = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.auditEmphasisOfMatters.update(auditEmphasisOfMatters => this.sortAuditEmphasisOfMatters([...auditEmphasisOfMatters, newAuditEmphasisOfMatter]));
    this.allAuditEmphasisOfMatters = this.sortAuditEmphasisOfMatters([...this.allAuditEmphasisOfMatters, newAuditEmphasisOfMatter]);

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createAuditEmphasisOfMatter({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit emphasis of matter created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.auditEmphasisOfMatters.update(auditEmphasisOfMatters => {
          const updated = auditEmphasisOfMatters.map(aeom => aeom.id === tempId ? response : aeom);
          return this.sortAuditEmphasisOfMatters(updated);
        });
        this.allAuditEmphasisOfMatters = this.sortAuditEmphasisOfMatters(
          this.allAuditEmphasisOfMatters.map(aeom => aeom.id === tempId ? response : aeom)
        );
      },
      error: (error) => {
        console.error('Error creating audit emphasis of matter:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.auditEmphasisOfMatters.update(auditEmphasisOfMatters =>
          auditEmphasisOfMatters.filter(aeom => aeom.id !== tempId)
        );
        this.allAuditEmphasisOfMatters = this.allAuditEmphasisOfMatters.filter(aeom => aeom.id !== tempId);
        this.toast.error(`<strong>Failed to create Audit Emphasis Of Matter:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onAuditEmphasisOfMatterCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalAuditEmphasisOfMatters = this.auditEmphasisOfMatters();

        // 1. Optimistically remove merged items from local state
        this.auditEmphasisOfMatters.update(auditEmphasisOfMatters =>
          auditEmphasisOfMatters.filter(aeom => !bulkMergeRequest.leftIds.includes(aeom.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeAuditEmphasisOfMattersBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Audit emphasis of matters merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadAuditEmphasisOfMatters();
          },
          error: (error) => {
            console.error('Error merging audit emphasis of matters:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.auditEmphasisOfMatters.set(originalAuditEmphasisOfMatters);
            // Restore selection state for retry
            this.rightItem = originalAuditEmphasisOfMatters.find(aeom => aeom.id === bulkMergeRequest.rightId);
            this.matchedItems = originalAuditEmphasisOfMatters.filter(aeom => bulkMergeRequest.leftIds.includes(aeom.id));
            this.toast.error(`<strong>Failed to merge Audit Emphasis Of Matters:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onReadyToMatch(event: {rightItem?: AuditEmphasisOfMatter, matchedItems?: AuditEmphasisOfMatter[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.spinnerService.show();
    setTimeout(() => {
      if (searchFilter.name) {
        const filtered = this.allAuditEmphasisOfMatters.filter(item =>
          item.name.toLowerCase().includes(searchFilter.name?.toLowerCase() ?? '')
        );
        this.auditEmphasisOfMatters.set(filtered);
      } else {
        this.auditEmphasisOfMatters.set(this.allAuditEmphasisOfMatters);
      }
      this.spinnerService.hide();
    }, 1500);
  }
}
